import React from "react";
import Recaptcha from "./Recaptcha.jsx";
import date from "./dateAdapter.js";
import * as http from "./http.ts";
import StripePaymentForm from "./pay_stripe.jsx";
import * as utils from "./utils.jsx";
import * as workparty from "./workparty.jsx";

import { useBackendData, useFormState } from "./hooks.ts";

var FormField = utils.FormField;

function RegistrationHeader({ wp, user, currentStep }) {
  var primary_trail_segment = utils.wpPrimarySegment(wp);
  var is_member = utils.isMember(user);
  var requires_payment = is_member ? wp.member_fee : wp.non_member_fee;

  var steps = [
    { id: "identify", label: "Identify" },
    { id: "register", label: "Register" },
  ];
  if (requires_payment) {
    steps.push({ id: "payment", label: "Payment" });
  }
  steps.push({ id: "confirm", label: "Confirm" });

  return (
    <div>
      <h1>
        Work Party: <a href={wp.path}>{wp.name}</a>
      </h1>
      <div className="meta">
        <h2>
          <span className="ico-svg">
            <img src={window.base_url + "static/svg/calendar.svg"} width="20" />
          </span>{" "}
          <utils.DateRange start={wp.start_date_time} end={wp.end_date_time} />
        </h2>
        {primary_trail_segment && primary_trail_segment.hiking_guide_region ? (
          <h5>
            Location:
            <br />
            <a>
              {primary_trail_segment.hiking_guide_region}&nbsp;
              {primary_trail_segment.hiking_guide_sub_region
                ? " — " + primary_trail_segment.hiking_guide_sub_region
                : ""}
            </a>
          </h5>
        ) : (
          ""
        )}
        <h5>
          Type:
          <br />
          <a>{wp.work_party_type.name}</a>
        </h5>
      </div>
      <div className="ui progress">
        {steps.map((step, i) => (
          <div
            key={i}
            className={"bar" + (step.id === currentStep ? " on" : "")}
          >
            {i + 1}. {step.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactSelector({
  wp,
  registration,
  user,
  updateRegistration,
  setStep,
}) {
  // If logged in, fetch dependents from Plone.
  const url =
    user && wp.age_restriction !== "Adults Only"
      ? window.cms_base_url + "vms-user"
      : null;
  const dependents =
    useBackendData({
      url,
      withCredentials: true,
      afterLoad,
    })?.dependents || [];

  function afterLoad(data) {
    // if user is logged in, has no dependents and we know their age,
    // skip to next step
    if (user && registration.known_age && !data.dependents.length) {
      handleSubmit();
    }
  }

  const {
    value,
    error,
    errors,
    submitting,
    setRequestState,
    handleChange,
    handleSubmit,
  } = useFormState({
    initialValue: { ...registration, dependents: [] },
    validate,
    handleSubmitted,
  });

  function renderSelector() {
    var name = value.firstName + " " + value.lastName;
    if (!user) {
      return (
        <ol>
          <li>
            <FormField
              type="text"
              name="firstName"
              label="First Name"
              placeholder="What's your first name?"
              required
              value={value.firstName}
              errors={errors}
              onChange={handleChange}
            />
          </li>
          <li>
            <FormField
              type="text"
              name="lastName"
              label="Last Name"
              placeholder="Last name"
              required
              value={value.lastName}
              errors={errors}
              onChange={handleChange}
            />
          </li>
        </ol>
      );
    } else if (dependents.length) {
      // has dependents
      var options = dependents.map((dependent) => {
        return {
          key: dependent.contact_id,
          value: dependent,
          label: dependent.first_name + " " + dependent.last_name,
        };
      });
      return (
        <div>
          <label>Choose the people you are registering:</label>
          <div>
            <FormField
              type="checkbox"
              name="registerSelf"
              label={name}
              inline={true}
              checked={value.registerSelf}
              errors={errors}
              onChange={handleChange}
            />
            <FormField
              type="checkboxes"
              name="dependents"
              options={options}
              inline={true}
              value={value.dependents}
              errors={errors}
              onChange={handleChange}
            />
          </div>
          <br />
        </div>
      );
    } else {
      // single logged-in user
      return (
        <ol>
          <li>
            <div className="field">
              <label>Name</label>
              <div>{name}</div>
            </div>
          </li>
        </ol>
      );
    }
  }

  function validate() {
    const errors = {};

    if (!value.registerSelf && !value.dependents.length) {
      errors.__form__ = "Please choose at least one person.";
    }
    if (
      !user &&
      value.under18 &&
      !(
        wp.work_party_type.name.indexOf("Youth Day") >= 0 &&
        (!wp.group_wp_open_to_public || value.groupId)
      )
    ) {
      errors.__form__ =
        'To register as a youth, your parent/guardian must add a dependent account for you. Read more about <a href="https://www.wta.org/our-work/next-generation/youth-volunteer-vacations#application-process">creating dependent accounts</a>.';
    }

    if (wp.age_restriction === "Youth Only") {
      if (
        (value.registerSelf && !value.under18) ||
        value.dependents.some((dep) => utils.age(dep.birthdate) >= 18)
      ) {
        errors.__form__ = "Registrants for this work party must be under 18.";
      }
    } else if (wp.age_restriction === "Adults Only") {
      if (
        (value.registerSelf && value.under18) ||
        value.dependents.some((dep) => utils.age(dep.birthdate) < 18)
      ) {
        errors.__form__ =
          "Registrants for this work party must be 18 or older.";
      }
    }
    return errors;
  }

  function handleSubmitted() {
    const form = document.getElementById("registration-contact-form");
    if (!form || form.checkValidity()) {
      if (user) {
        checkDocuments();
      } else {
        selectContact();
      }
    }
  }

  const [documents, setDocuments] = React.useState([]);

  function checkDocuments() {
    setRequestState((current) => ({ ...current, submitting: true }));
    var contacts = [];
    if (value.registerSelf) {
      contacts.push(user.contact_id);
    }
    if (value.dependents?.length) {
      contacts = contacts.concat(
        value.dependents.map((dependent) => dependent.contact_id),
      );
    }
    http
      .getJson(wp.views.documents, { query: { c: contacts } })
      .then((data) => {
        var incomplete = false;
        data.forEach((wrapper) => {
          wrapper.documentInfos.forEach((doc) => {
            if (!doc.completed) {
              incomplete = true;
            }
          });
        });
        if (incomplete) {
          setDocuments(data);
        } else {
          var awardInfo = {};
          data.forEach((wrapper) => {
            awardInfo[wrapper.contactId] = wrapper;
          });
          selectContact(awardInfo);
        }
      });
  }

  function selectContact(awardInfoByContact) {
    awardInfoByContact = awardInfoByContact || {};
    var awardInfo;
    let reg = value;
    if (user) {
      if (reg.registerSelf) {
        // if user is registering themself and has ACL or higher permission,
        // register them with the ACL role.
        if (
          (user.role === "Assistant Crew Leader" ||
            user.role === "Crew Leader" ||
            user.role === "Admin") &&
          wp.work_party_type.acl_self_registration_allowed
        ) {
          reg.workPartyRole = "Assistant Crew Leader";
        }
        awardInfo = awardInfoByContact[user.contact_id];
      } else {
        // convert first dependent to main registrant
        const [mainRegistrant, ...dependents] = reg.dependents;
        const {
          email: _email,
          streetAddress: _streetAddress,
          city: _city,
          state: _state,
          zip: _zip,
          phone: _phone,
          phoneType: _phoneType,
          ...registrationWithoutAddress
        } = reg;
        reg = {
          ...registrationWithoutAddress,
          dependents,
          contactId: mainRegistrant.contact_id,
          under18: utils.age(mainRegistrant.birthdate) < 18,
          firstName: mainRegistrant.first_name,
          lastName: mainRegistrant.last_name,
        };

        awardInfo =
          awardInfoByContact[mainRegistrant.contact_id.substring(0, 15)];
      }

      // convert dependents to 'others'
      reg.others = reg.dependents.map((d) => ({
        contactId: d.contact_id,
        firstName: d.first_name,
        lastName: d.last_name,
        under18: utils.age(d.birthdate) < 18,
      }));
    }
    const { dependents: _dependents, ...registration } = reg;
    updateRegistration(registration, awardInfo);
    setStep("register");
  }

  var login_url = window.cms_base_url + "/login";
  return (
    <div>
      {user ? (
        <h2>You are signed in as {user.screen_name}</h2>
      ) : (
        <div className="module msg">
          <div className="container">
            <header>
              <h2>Hello!</h2>
            </header>
            <div className="row">
              <div className="col eight">
                <h2>You are registering as a GUEST.</h2>
                <h3>Already have an account?</h3>
              </div>
              <div className="col four">
                <a href={login_url} className="ui button green">
                  Sign in
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      <RegistrationHeader wp={wp} user={user} currentStep="identify" />
      <section className="main">
        <article className="wrapper">
          <section className="utility">
            <form
              className="utility"
              id="registration-contact-form"
              onSubmit={handleSubmit}
            >
              {documents?.length ? (
                <workparty.WorkPartyPreregDocuments
                  wp={wp}
                  user={user}
                  data={documents}
                />
              ) : (
                <div>
                  <fieldset>{renderSelector()}</fieldset>
                  {value.registerSelf && !value.known_age ? (
                    <div className="row">
                      <fieldset className="col two">
                        <ol>
                          <li></li>
                        </ol>
                      </fieldset>
                      <fieldset className="col three">
                        <FormField
                          type="checkbox"
                          name="under18"
                          label="I am under 18."
                          checked={value.under18}
                          errors={errors}
                          onChange={handleChange}
                        />
                      </fieldset>
                    </div>
                  ) : (
                    ""
                  )}
                  <button
                    type="submit"
                    className="ui button"
                    disabled={submitting}
                  >
                    {submitting ? "Checking documents..." : "Continue"}
                  </button>
                  {error ? (
                    <div
                      className="validation-error"
                      dangerouslySetInnerHTML={{ __html: error }}
                    />
                  ) : (
                    ""
                  )}
                </div>
              )}
              <br />
            </form>
          </section>
        </article>
      </section>
    </div>
  );
}

// Other registrants
function OtherRegistrant({
  prefix,
  use_member_fee,
  initialValue,
  errors,
  name,
  onChange,
  onRemove,
}) {
  const { value, handleChange } = useFormState({
    initialValue,
    onChange,
    name,
  });

  return (
    <div className="row">
      <fieldset className="col two">
        <FormField
          type="text"
          name={`${prefix}.firstName`}
          placeholder="First name"
          required
          value={value.firstName}
          errors={errors}
          onChange={handleChange}
        />
      </fieldset>
      <fieldset className="col two">
        <FormField
          type="text"
          name={`${prefix}.lastName`}
          placeholder="Last name"
          required
          value={value.lastName}
          errors={errors}
          onChange={handleChange}
        />
      </fieldset>
      <fieldset className="col three">
        <ol>
          <li>
            <FormField
              type="email"
              name={`${prefix}.email`}
              placeholder="Email"
              required
              value={value.email}
              errors={errors}
              onChange={handleChange}
            />
          </li>
        </ol>
      </fieldset>
      <fieldset
        className={"col three" + (use_member_fee ? " offset-field-height" : "")}
      >
        <FormField
          type="checkbox"
          name={`${prefix}.under18`}
          label="Under 18?"
          checked={value.under18}
          errors={errors}
          onChange={handleChange}
        />
        {use_member_fee ? (
          <FormField
            type="checkbox"
            name={`${prefix}.declaredInHH`}
            label="Household Member"
            checked={value.declaredInHH}
            errors={errors}
            onChange={handleChange}
          />
        ) : (
          ""
        )}
      </fieldset>
      <div className="col two">
        <button type="button" className="red ui button" onClick={onRemove}>
          Remove
        </button>
      </div>
    </div>
  );
}

function OtherRegistrants({
  use_member_fee,
  max,
  initialValue,
  errors,
  name,
  onChange,
}) {
  const { value, setValue, handleChange } = useFormState({
    initialValue: initialValue?.length ? initialValue : [{}],
    onChange,
    name,
  });

  function handleAdd() {
    const newValue = [...value, {}];
    setValue(newValue);
    onChange(newValue, name);
  }

  function handleRemove(i) {
    const newValue = value.filter((_item, index) => index !== i);
    setValue(newValue);
    onChange(newValue, name);
  }

  const error = errors ? errors[name] : null;
  return (
    <div className="field">
      {error ? <div className="module validation-error">{error}</div> : null}
      {value.map((v, k) =>
        v.contactId ? null : (
          <OtherRegistrant
            key={k}
            name={k}
            prefix={`${name}.${k}`}
            initialValue={v}
            errors={errors}
            use_member_fee={use_member_fee}
            onChange={handleChange}
            onRemove={handleRemove.bind(null, k)}
          />
        ),
      )}
      {value.length < max ? (
        <button type="button" className="green ui button" onClick={handleAdd}>
          Add Another Person
        </button>
      ) : (
        ""
      )}
    </div>
  );
}

var getDayOptions = function (wp) {
  var dayOptions = [];
  if (wp.work_party_type.allow_single_day_registration) {
    var start = date.startOfDay(date.toDate(wp.start_date_time));
    var end = date.startOfDay(date.toDate(wp.end_date_time));
    var dayCount =
      Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    if (dayCount > 1) {
      dayOptions = [
        { value: "Day 1", label: date.format(start, "EEEE") },
        { value: "Day 2", label: date.format(date.addDays(start, 1), "EEEE") },
      ];
    }
    if (dayCount > 2) {
      // max of 3 days currently supported
      dayOptions.push({
        value: "Day 3",
        label: date.format(date.addDays(start, 2), "EEEE"),
      });
    }
  }
  return dayOptions;
};

// Main registration form
function RegisterForm({
  wp,
  user,
  recaptcha_sitekey,
  requestedGroupId,
  place_vols_on_waitlist,
  place_acls_on_waitlist,
  awardInfo,
  awardCustomizations,
  action,
  initialValue,
  updateRegistration,
  setStripePaymentIntentClientSecret,
  setStep,
}) {
  const {
    value,
    setValue,
    handleChange,
    handleSubmit,
    error,
    errorData,
    errors,
    info,
    submitting,
    setRequestState,
  } = useFormState({
    action,
    initialValue: {
      ...initialValue,
      showMyBackpack: true,
      confirmingWaitlist: false,
      stripePaymentIntentClientSecret: null,
    },
    handleSubmitted: handleSubmitted,
  });

  function toggleMyBackpack() {
    setValue({ ...value, showMyBackpack: !value.showMyBackpack });
  }

  function handlePreSubmit(e) {
    e.preventDefault();
    if (recaptcha_sitekey && !user) {
      validateCaptcha();
    } else {
      handleSubmit();
    }
  }

  function handleSubmitted(data) {
    function id_trunc(id) {
      // Always use the 15 char id
      if (id) {
        return id.substr(0, 15);
      }
    }
    const otherResults = data.othersRegs || [];
    const reg = {
      ...value,
      id: id_trunc(data.regId),
      status: data.status,
      others: (value.others || []).map((other, index) => {
        const result = otherResults[index];
        return result
          ? {
              ...other,
              id: id_trunc(result.otherRegId),
              status: result.otherStatus,
              member_status: result.memberStatus,
              is_current_member: result.memberStatus == "Current",
              errors: result.otherErrorCode,
              additional_trip: result.previousVVThisYear,
            }
          : other;
      }),
    };
    var multiple_waitlist_option = false;
    if (
      reg.others &&
      reg.others.length &&
      reg.others.every((other) => !!other.email)
    ) {
      multiple_waitlist_option = true;
    }
    // if no multiple registrations, waitlist confirmation is implicit
    if (data.status === "Waitlisted" && multiple_waitlist_option) {
      setValue({ ...value, confirmingWaitlist: true });
    } else {
      updateRegistration(reg);
      if (data.status === "Payment Pending") {
        setStripePaymentIntentClientSecret(
          data.stripePaymentIntentClientSecret,
        );
        setStep("pay");
      } else {
        setStep("confirm");
      }
    }
  }

  function validateCaptcha() {
    var gresponse = window.grecaptcha.getResponse();
    var validate_url = `${window.base_url}workparty/${wp._id}/validate-captcha`;
    http
      .postForm(validate_url, { gresponse: gresponse })
      .then(function (data) {
        if (data.success) {
          handleSubmit();
        } else {
          setRequestState((currentState) => ({
            ...currentState,
            submitting: false,
            error: "You need to verify that you are not a robot.",
          }));
        }
      })
      .catch(function () {
        setRequestState((currentState) => ({
          ...currentState,
          submitting: false,
          error: "There was an error verifying the captcha.",
        }));
      });
  }

  function handleCancelWaitlist() {
    var action_url = window.base_url + "workparty/" + wp._id + "/cancel";
    var data = { regId: value.id };
    if (!user) {
      action_url = action_url + "_guest";
    }
    var others = value.others;
    if (others && others.length > 0) {
      data["otherRegs"] = others.map(function (r) {
        return r.id;
      });
    }
    http
      .postForm(action_url, data)
      .then(function () {
        setValue({ ...value, confirmingWaitlist: false });
        setRequestState((currentState) => ({
          ...currentState,
          submitting: false,
          info: "Your waitlisted registration has been canceled.",
        }));
      })
      .catch(function (error) {
        setRequestState((currentState) => ({
          ...currentState,
          ...error.state,
        }));
      });
  }

  function handleConfirmWaitlist() {
    if (value.splitParty) {
      // if user checked the 'split party' option, we need to update the registration to set that
      http
        .postForm(wp.path + "waitlist-flag", {
          regId: value.id,
        })
        .then(function () {
          setStep("confirm");
        })
        .catch(function (error) {
          setRequestState((currentState) => ({
            ...currentState,
            ...error.state,
          }));
        });
    } else {
      setStep("confirm");
    }
  }

  var is_member = utils.isMember(user);
  var role = user ? user.role : null;
  var isGreenhorn =
    user && user.incentive_days <= wp.max_incentive_days_for_conditional_vols;
  var greenhorn_date = date.subDays(
    date.toDate(wp.start_date_time),
    wp.days_before_conditional_spots_expire,
  );
  var isOpenForGreenhorns = date.now().getTime() <= greenhorn_date.getTime();
  const reg = value;
  var requires_payment = is_member ? wp.member_fee : wp.non_member_fee;
  var FormField = utils.FormField;
  var dayOptions = getDayOptions(wp);
  var maxFriends = wp.work_party_type.max_friends_family;
  var group = null;
  var isPrivateGroupWP = wp.work_party_groups && !wp.group_wp_open_to_public;
  if (requestedGroupId) {
    group = wp.work_party_groups.map(function (g) {
      if (g._id == requestedGroupId) {
        return (
          <p>
            You are registering for the <strong>{g.name}</strong> group.
          </p>
        );
      }
    });
  }
  var registrants = [reg.firstName + " " + reg.lastName];
  reg.others.forEach((other) => {
    if (other.contactId) {
      registrants.push(other.firstName + " " + other.lastName);
    }
  });

  var place_on_waitlist;
  if (reg.workPartyRole === "Volunteer") {
    place_on_waitlist = place_vols_on_waitlist;
  } else {
    place_on_waitlist = place_acls_on_waitlist;
  }
  if (
    wp.conditional_spots_held_for_volunteers > 0 &&
    isGreenhorn &&
    wp.remaining_spots_for_new_volunteers > 0 &&
    isOpenForGreenhorns &&
    !wp.set_wp_to_full
  ) {
    place_on_waitlist = false;
  }
  var buttonText;
  if (place_on_waitlist) {
    buttonText = "Join Waitlist";
  } else if (wp.difficulty === "Strenuous") {
    buttonText = "Apply";
  } else {
    buttonText = "Register";
  }
  var button = (
    <button className="button" onClick={handlePreSubmit} disabled={submitting}>
      {submitting ? "Submitting..." : buttonText}
    </button>
  );

  // Award customization:
  // Find first award that the user has enough incentive days for
  // but has not already been awarded.
  let awardCustomization;
  if (awardInfo) {
    var anticipatedIncentiveDays =
      awardInfo.pastAndUpcomingIncentiveDays + wp.incentive_days;
    for (var i = 0; i < awardCustomizations.length; i++) {
      var award = awardCustomizations[i];
      if (award.collect_personalization_on_day_x > anticipatedIncentiveDays) {
        continue;
      }
      if (awardInfo.receivedAwardIds.includes(award.id)) {
        continue;
      }
      awardCustomization = award;
      break;
    }
  }
  return (
    <div>
      <RegistrationHeader wp={wp} user={user} currentStep="register" />
      <section className="main">
        <article className="wrapper">
          <section className="utility">
            {group}
            <form className="utility">
              {reg.registerSelf &&
              (role == "Assistant Crew Leader" ||
                role == "Crew Leader" ||
                role == "Admin") &&
              wp.work_party_type.acl_self_registration_allowed ? (
                <div>
                  <h2>Choose your role:</h2>
                  <div className="row">
                    <div className="col four">
                      <FormField
                        type="select"
                        name="workPartyRole"
                        value={reg.workPartyRole}
                        errors={errors}
                        onChange={handleChange}
                      >
                        {!isPrivateGroupWP ? (
                          <option value="Volunteer">Crew Member</option>
                        ) : (
                          ""
                        )}
                        <option value="Assistant Crew Leader">
                          Assistant Crew Leader
                        </option>
                      </FormField>
                    </div>
                    <div className="col eight">
                      <p className="side_cta">
                        Do we owe you an{" "}
                        <a
                          target="_blank"
                          href={
                            window.cms_base_url +
                            "volunteer/trail-volunteer-benefits"
                          }
                        >
                          award
                        </a>
                        ?<br />
                        Please let us know by leaving a comment below.
                      </p>
                    </div>
                  </div>
                  <br />
                  <hr />
                </div>
              ) : (
                ""
              )}
              <div className="row">
                <fieldset className="col twelve">
                  <ol>
                    <li>
                      <label>Registrants</label>
                      <p>{registrants.join(", ")}</p>
                    </li>
                  </ol>
                </fieldset>
              </div>
              {reg.registerSelf && (
                <div>
                  <hr />
                  <div className="row">
                    <fieldset className="col eight">
                      <ol>
                        <li>
                          <FormField
                            type="email"
                            name="email"
                            label="Email"
                            placeholder="email@email.com"
                            required
                            value={reg.email}
                            errors={errors}
                            onChange={handleChange}
                          />
                        </li>
                      </ol>
                    </fieldset>
                  </div>
                  <div className="row">
                    <fieldset className="col eight">
                      <ol>
                        <li>
                          <FormField
                            type="tel"
                            name="phone"
                            label="Phone"
                            placeholder="000-000-0000"
                            required
                            value={reg.phone}
                            errors={errors}
                            onChange={handleChange}
                          />
                        </li>
                      </ol>
                    </fieldset>
                    <fieldset className="col four">
                      <ol>
                        <li>
                          <FormField
                            type="select"
                            name="phoneType"
                            value={reg.phoneType}
                            errors={errors}
                            onChange={handleChange}
                          >
                            <option value="Home">Home Phone</option>
                            <option value="Mobile">Mobile/Cell</option>
                            <option value="Work">Work Phone</option>
                          </FormField>
                        </li>
                      </ol>
                    </fieldset>
                  </div>
                  <hr />
                  <div className="row">
                    <fieldset className="col eight">
                      <ol>
                        <li>
                          <FormField
                            type="text"
                            name="streetAddress"
                            label="Address"
                            placeholder="Street #/Apt."
                            required
                            value={reg.streetAddress}
                            errors={errors}
                            onChange={handleChange}
                          />
                        </li>
                      </ol>
                    </fieldset>
                    <fieldset className="col four">
                      <ol>
                        <li>
                          <FormField
                            type="select"
                            name="state"
                            value={reg.state}
                            errors={errors}
                            onChange={handleChange}
                          >
                            {utils.STATES.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </FormField>
                        </li>
                      </ol>
                    </fieldset>
                  </div>
                  <div className="row">
                    <fieldset className="col eight">
                      <ol>
                        <li>
                          <FormField
                            type="text"
                            name="city"
                            label="City"
                            placeholder="City"
                            required
                            value={reg.city}
                            errors={errors}
                            onChange={handleChange}
                          />
                        </li>
                      </ol>
                    </fieldset>
                    <fieldset className="col four align-right">
                      <ol>
                        <li>
                          <FormField
                            type="text"
                            name="zip"
                            label="Zip"
                            placeholder="90210"
                            required
                            value={reg.zip}
                            errors={errors}
                            onChange={handleChange}
                          />
                        </li>
                      </ol>
                    </fieldset>
                  </div>
                </div>
              )}
              {dayOptions.length ? (
                <div>
                  <hr />
                  <br />
                  <br />
                  <div className="row">
                    <div className="col two">
                      <h2>Which days?</h2>
                    </div>
                    <div className="col ten">
                      <FormField
                        type="checkboxes"
                        name="daysRegisteredFor"
                        options={dayOptions}
                        inline={true}
                        value={reg.daysRegisteredFor}
                        errors={errors}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                ""
              )}
              <hr />
              <br />
              {wp.carpooling_enabled ? (
                <div>
                  <h2>Carpool</h2>
                  <div className="row">
                    <div className="col four">
                      <FormField
                        type="select"
                        name="carPool"
                        value={reg.carPool}
                        errors={errors}
                        onChange={handleChange}
                      >
                        <option>No Thanks</option>
                        <option>Needs a ride</option>
                        <option>Willing to drive</option>
                        <option>Willing to drive or ride</option>
                      </FormField>
                    </div>
                    <div className="col eight">
                      <FormField
                        type="text"
                        name="carPoolNotes"
                        placeholder="Message"
                        value={reg.carPoolNotes}
                        errors={errors}
                        onChange={handleChange}
                      />
                      <em>
                        Please list your preferred meeting point such as a park
                        &amp; ride, neighborhood or town.
                      </em>
                    </div>
                  </div>
                  <br />
                  <br />
                  <hr />
                  <br />
                </div>
              ) : (
                ""
              )}
              {maxFriends ? (
                <div>
                  <h2>Bringing someone with you?</h2>
                  <p>All additional registrants must have unique emails</p>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: wp.work_party_type.friends_family_help_text,
                    }}
                  />
                  <OtherRegistrants
                    name="others"
                    max={maxFriends}
                    initialValue={reg.others}
                    errors={errors}
                    onChange={handleChange}
                    use_member_fee={is_member && requires_payment}
                  />
                  <br />
                  <br />
                  <hr />
                </div>
              ) : (
                ""
              )}
              {awardCustomization &&
              awardCustomization.additional_info_needed === "Nickname" &&
              !awardInfo.hardHatNickname ? (
                <div>
                  <h2>Congratulations!</h2>
                  <p>
                    Our records show that you’re approaching a new milestone. We
                    hope to have your award ready at an upcoming work party.
                  </p>
                  <div className="row">
                    <div className="col two">
                      <label htmlFor="hardHatName">Nickname for Hard Hat</label>
                    </div>
                    <div className="col ten">
                      <FormField
                        type="text"
                        name="hardHatName"
                        placeholder="Your Nickname"
                        required
                        errors={errors}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <br />
                  <br />
                  <hr />
                </div>
              ) : (
                ""
              )}
              {awardCustomization &&
              awardCustomization.additional_info_needed === "Size" &&
              !awardInfo.vestSize ? (
                <div>
                  <h2>Congratulations!</h2>
                  <p>
                    Our records show that you’re approaching a new milestone. We
                    hope to have your award ready at an upcoming work party.
                  </p>
                  <div className="row">
                    <div className="col two">
                      <label htmlFor="vestSize">Vest Size</label>
                    </div>
                    <div className="col ten">
                      <FormField
                        type="select"
                        name="vestSize"
                        errors={errors}
                        onChange={handleChange}
                      >
                        <option>XS</option>
                        <option>S</option>
                        <option>M</option>
                        <option>L</option>
                        <option>XL</option>
                        <option>XXL</option>
                      </FormField>
                    </div>
                  </div>
                  <br />
                  <br />
                  <hr />
                </div>
              ) : (
                ""
              )}
              <h2>
                Have a question, comment or award inquiry about this work party?
              </h2>
              <FormField
                type="textarea"
                name="comments"
                value={reg.comments}
                errors={errors}
                onChange={handleChange}
              />
              <br />
              <br />
              <br />
              {!user ? (
                <div className="module highlight">
                  <div className="row">
                    <fieldset>
                      <div className="col twelve">
                        <h2>
                          Create My Backpack Account? &nbsp;
                          <div
                            className="custom_checkbox"
                            style={{ display: "inline" }}
                          >
                            <input
                              id="toggleMyBackpack"
                              type="checkbox"
                              onChange={toggleMyBackpack}
                              checked={value.showMyBackpack}
                            />
                            <label htmlFor="toggleMyBackpack" />
                          </div>
                        </h2>
                        <hr />
                        <h3>Why Create an Account?</h3>
                        <p>
                          With an account, you’ll be able to manage or cancel
                          your work parties, track your days on trail, file trip
                          reports or make notes on trails you’ve saved to your
                          My Backpack.
                        </p>
                      </div>
                    </fieldset>
                  </div>
                  <div
                    className={"toggler" + (value.showMyBackpack ? " on" : "")}
                  >
                    <div className="row">
                      <fieldset className="col twelve">
                        <FormField
                          name="screenName"
                          type="text"
                          label="Screen Name"
                          placeholder="Choose your public name"
                          help="This will appear on your My Backpack page, on Trip Reports you write and on any comments you post at wta.org."
                          value={reg.screenName}
                          errors={errors}
                          onChange={handleChange}
                        />
                      </fieldset>
                    </div>
                    <p>
                      We'll send you an email to confirm your address and then
                      you can set your password.
                      <br />
                      <br />
                    </p>
                  </div>
                </div>
              ) : (
                ""
              )}
              <hr />
              {recaptcha_sitekey && !user ? (
                <div>
                  <Recaptcha sitekey={recaptcha_sitekey} />
                  <br />
                  <br />
                </div>
              ) : (
                ""
              )}
              <div className="row">
                <fieldset className="col twelve">
                  <FormField
                    type="checkbox"
                    name="waiverSigned"
                    label={
                      <span>
                        I've read and agree to the terms of the{" "}
                        <a target="_blank" href={wp.views.waiver}>
                          release waiver
                        </a>
                        .
                      </span>
                    }
                    checked={reg.waiverSigned}
                    errors={errors}
                    onChange={handleChange}
                  />
                </fieldset>
              </div>
              <hr />
              <div className="ui">{button}</div>
              <br />
              {value.confirmingWaitlist ? (
                <div className="alert module">
                  <div className="container">
                    <header>
                      <h2>Join waitlist?</h2>
                    </header>
                    <p>
                      Unfortunately this work party no longer has enough spots
                      open for you to register, so we have added you to the
                      waitlist.
                    </p>
                    {reg.others &&
                    reg.others.length &&
                    reg.others.every((other) => !!other.email) ? (
                      <div className="row">
                        <div className="col twelve">
                          <FormField
                            type="checkbox"
                            name="splitParty"
                            label="Check this box if you want each person in your party to join the waitlist separately. Each person may receive an invitation to register at a different time as spots become available. Leave it unchecked if you want to join the waitlist as an entire party."
                            checked={reg.splitParty}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    ) : (
                      ""
                    )}
                    <div className="row">
                      <div className="col six"></div>
                      <div className="col six">
                        <button
                          type="button"
                          className="ui button"
                          onClick={handleCancelWaitlist}
                        >
                          Cancel Registration
                        </button>{" "}
                        <button
                          type="button"
                          className="ui button"
                          onClick={handleConfirmWaitlist}
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                ""
              )}
              {info ? (
                <div className="info">
                  <div className="container">{info}</div>
                </div>
              ) : (
                ""
              )}
              {error ? (
                <div className="validation-error">
                  <div className="container">
                    {error}
                    {errorData?.conflictingWorkParties
                      ? errorData.conflictingWorkParties.map(
                          (conflict, index) => (
                            <div key={index}>
                              <a
                                href={
                                  window.base_url +
                                  "workparty/" +
                                  conflict.wpID.substring(0, 15) +
                                  "/"
                                }
                              >
                                {conflict.name}
                              </a>
                            </div>
                          ),
                        )
                      : ""}
                  </div>
                </div>
              ) : (
                ""
              )}
              <br />
            </form>
          </section>
        </article>
      </section>
    </div>
  );
}

// registration confirmation / thank you page
function RegisterConfirmation({ wp, user, registration }) {
  const reg = registration;
  var wp_url = window.base_url + "workparty/" + wp._id;
  return (
    <div>
      <section className="main">
        <article>
          <section className="layout primary">
            <header role="heading">
              <h1>
                {reg.status === "Applied" ? "Thanks for Applying" : "Thank You"}
              </h1>
              <h2>
                {reg.status === "Applied"
                  ? "Application Summary"
                  : "Registration Summary"}
              </h2>
            </header>
            <hr />
            {reg.status === "Waitlisted" ? (
              <h3>We have sent an email with your wait list information.</h3>
            ) : (
              ""
            )}
            {reg.status === "Applied" ? (
              <div>
                <h3>We have sent an email with more information to you.</h3>
                <p>
                  WTA values the safety of our volunteers, and we strive to
                  maintain the safest working conditions possible on our
                  volunteer work parties. This includes work parties that are
                  considered strenuous.
                  <strong>
                    {" "}
                    For your safety, we will be screening each individual who
                    applies to this work party before inviting them to join the
                    crew.{" "}
                  </strong>
                  For strenuous work parties we'll review the information you
                  provided in your backcountry experience form, and may give you
                  a call to discuss any questions or clarifications we might
                  have. You should hear back from us about joining this trip
                  within two weeks of application submission.
                </p>
              </div>
            ) : (
              ""
            )}
            {reg.status !== "Waitlisted" && reg.status !== "Applied" ? (
              <h3>
                We have sent an email with your registration details to you.
              </h3>
            ) : (
              ""
            )}
            <a
              onClick={(event) => {
                event.preventDefault();
                window.print();
              }}
              href="#"
              className="ui button small"
            >
              <span className="icon-print"></span> Print This Page
            </a>
          </section>
          <section className="layout secondary">
            <div className="module dialogue">
              <div className="bubble">
                <p>
                  {reg.status === "Applied"
                    ? "Thank you for applying,"
                    : "Congratulations,"}{" "}
                  {reg.firstName}!<br />
                  {reg.status === "Waitlisted"
                    ? "You are now on the waitlist."
                    : ""}
                  {reg.status !== "Applied" && reg.status !== "Waitlisted"
                    ? "You're all signed up."
                    : ""}
                </p>
              </div>
              <img src={window.base_url + "static/images/avatar.01.png"} />
            </div>
          </section>
        </article>
      </section>
      <section>
        <div className="wrapper">
          <table>
            <tbody>
              <tr>
                <th>Work Party</th>
                <th>Date(s)</th>
                {wp.documents && wp.documents.length ? (
                  <th>Required Docs</th>
                ) : (
                  ""
                )}
                <th>Registration Status</th>
              </tr>
              <tr>
                <td>
                  <a href={wp.path}>{wp.name}</a> &mdash;{" "}
                  {wp.work_party_type.name}
                </td>
                <td>
                  <utils.DateRange
                    start={wp.start_date_time}
                    end={wp.end_date_time}
                  />
                </td>
                {wp.documents && wp.documents.length ? (
                  <td>
                    {wp.documents.map((doc) => (
                      <div>
                        <span className="icon-doc-text"></span>{" "}
                        {doc.document_display_name}
                      </div>
                    ))}
                    {user && (
                      <a
                        href={wp.path + "#documents"}
                        className="ui button green"
                      >
                        View document status
                      </a>
                    )}
                  </td>
                ) : (
                  ""
                )}
                <td>{reg.status}</td>
              </tr>
            </tbody>
          </table>
          <h3>Add to Calendar</h3>
          <div>
            <a
              className="ui button orange calendar-button google-calendar"
              target="_blank"
              href={wp_url + "/google-calendar-link"}
            >
              Google Calendar
            </a>
            <a
              className="ui button orange calendar-button"
              href={wp_url + "/event.ics"}
            >
              Outlook/iCal (ics)
            </a>
          </div>
        </div>
      </section>
      <hr />
      <a href={window.base_url} className="ui button green">
        Back to Find a Work Party
      </a>
      <br />
      <br />
    </div>
  );
}

// main registration component which controls progression through the above steps
function RegisterFlow({
  wp,
  user,
  registration,
  under18,
  requestedGroupId,
  useMemberPricing,
  scholarship_amount_awarded,
  recaptcha_sitekey,
  place_vols_on_waitlist,
  place_acls_on_waitlist,
  awardCustomizations,
  step,
  stripePublishableKey,
  stripePaymentIntentClientSecret = null,
}) {
  function getInitialState() {
    let reg = registration;
    if (reg === undefined) {
      reg = {
        workPartyRole: "Volunteer",
        phoneType: "Mobile",
        carPool: user ? user.carpool_pref || "No Thanks" : "No Thanks",
        carPoolNotes: user ? user.carpool_notes || "" : "",
        under18: under18,
        known_age: false,
        waiverSigned: false,
        groupId: requestedGroupId,
        registerSelf: true,
        dependents: [],
        others: [],
      };
      if (user) {
        reg.firstName = user.first_name;
        reg.lastName = user.last_name;
        reg.email = user.email;
        reg.streetAddress = user.streetAddress;
        reg.city = user.city;
        reg.state = user.state;
        reg.zip = user.zip;
        reg.phone = user.phone;
        reg.phoneType = user.phoneType || "Mobile";
        reg.known_age = user.birthdate !== null;
      }
      var dayOptions = getDayOptions(wp);
      if (dayOptions.length) {
        reg.daysRegisteredFor = dayOptions.map((o) => o.value);
      }
    }
    return {
      registration: reg,
      awardInfo: null,
      step: step || "identify",
      stripePaymentIntentClientSecret,
    };
  }

  const [state, setState] = React.useState(getInitialState);

  const previousStepRef = React.useRef(state.step);
  React.useEffect(() => {
    if (previousStepRef.current !== state.step) {
      window.scrollTo({ top: 0 });
      previousStepRef.current = state.step;
    }
  }, [state.step]);

  function updateRegistration(value, awardInfo) {
    setState((currentState) => ({
      ...currentState,
      registration: value,
      awardInfo: awardInfo,
    }));
  }

  function setStripePaymentIntentClientSecret(secret) {
    setState((currentState) => ({
      ...currentState,
      stripePaymentIntentClientSecret: secret,
    }));
  }

  function setStep(step) {
    setState((currentState) => ({ ...currentState, step: step }));
  }

  switch (state.step) {
    case "login":
      var url = window.location.href;
      window.location.href =
        window.cms_base_url + "/login?came_from=" + encodeURIComponent(url);
      return null;
    case "pay":
      return (
        <StripePaymentForm
          wp={wp}
          user={user}
          registration={state.registration}
          updateRegistration={updateRegistration}
          setStep={setStep}
          useMemberPricing={useMemberPricing}
          scholarship_amount_awarded={scholarship_amount_awarded}
          stripePublishableKey={stripePublishableKey}
          stripePaymentIntentClientSecret={
            state.stripePaymentIntentClientSecret
          }
        />
      );
    case "confirm":
      return (
        <RegisterConfirmation
          wp={wp}
          user={user}
          registration={state.registration}
        />
      );
    case "register":
      return (
        <RegisterForm
          wp={wp}
          user={user}
          recaptcha_sitekey={recaptcha_sitekey}
          initialValue={state.registration}
          requestedGroupId={requestedGroupId}
          place_vols_on_waitlist={place_vols_on_waitlist}
          place_acls_on_waitlist={place_acls_on_waitlist}
          awardInfo={state.awardInfo}
          awardCustomizations={awardCustomizations}
          updateRegistration={updateRegistration}
          action={wp.actions.register}
          setStep={setStep}
          setStripePaymentIntentClientSecret={
            setStripePaymentIntentClientSecret
          }
        />
      );
    default:
      return (
        <ContactSelector
          wp={wp}
          registration={state.registration}
          user={user}
          updateRegistration={updateRegistration}
          setStep={setStep}
        />
      );
  }
}

export default RegisterFlow;
